import * as Sentry from '@sentry/react'
import { platform } from '../../device/platform'

export type InitStep =
  | 'sentry_init'
  | 'tenant_init'
  | 'event_tracker_init'
  | 'filesystem_init'
  | 'session_migration'
  | 'session_restore'
  | 'set_user_cache'
  | 'discovery'
  | 'mobile_init'
  | 'mobile_validate_modules'
  | 'mobile_aura_initialize'
  | 'mobile_ws_start'
  | 'mobile_onesignal'
  | 'deep_links'
  | 'service_worker'
  | 'read_modules_device'
  | 'global_variables'
  | 'static_module_load'
  | 'bootstrap'
  | 'pre_init_complete'

interface StepTiming {
  step: InitStep
  startedAt: number
  finishedAt?: number
  durationMs?: number
  success: boolean
  error?: string
  metadata?: Record<string, any>
}

class InitTracker {
  private steps: StepTiming[] = []
  private globalStart: number = 0
  private currentStep: StepTiming | null = null

  constructor () {
    this.globalStart = performance.now()
  }

  /**
   * Marca el inicio de un paso de inicialización.
   */
  startStep (step: InitStep, metadata?: Record<string, any>): void {
    this.currentStep = {
      step,
      startedAt: performance.now(),
      success: false,
      metadata
    }
  }

  /**
   * Marca un paso como completado exitosamente.
   */
  endStep (step: InitStep, metadata?: Record<string, any>): void {
    const timing = this.currentStep?.step === step ? this.currentStep : undefined
    const now = performance.now()

    const entry: StepTiming = {
      step,
      startedAt: timing?.startedAt ?? now,
      finishedAt: now,
      durationMs: timing ? now - timing.startedAt : 0,
      success: true,
      metadata: { ...timing?.metadata, ...metadata }
    }

    this.steps.push(entry)
    this.currentStep = null

    Sentry.addBreadcrumb({
      category: 'init',
      message: `[init:${step}] OK (${entry.durationMs?.toFixed(0)}ms)`,
      level: 'info',
      data: { durationMs: entry.durationMs, ...entry.metadata }
    })
  }

  /**
   * Marca un paso como fallido y reporta a Sentry.
   */
  failStep (step: InitStep, error: unknown, metadata?: Record<string, any>): void {
    const timing = this.currentStep?.step === step ? this.currentStep : undefined
    const now = performance.now()
    const errorMessage = error instanceof Error ? error.message : String(error)
    const errorStack = error instanceof Error ? error.stack : undefined

    const entry: StepTiming = {
      step,
      startedAt: timing?.startedAt ?? now,
      finishedAt: now,
      durationMs: timing ? now - timing.startedAt : 0,
      success: false,
      error: errorMessage,
      metadata: { ...timing?.metadata, ...metadata }
    }

    this.steps.push(entry)
    this.currentStep = null

    Sentry.addBreadcrumb({
      category: 'init',
      message: `[init:${step}] FAILED (${entry.durationMs?.toFixed(0)}ms)`,
      level: 'error',
      data: { durationMs: entry.durationMs, error: errorMessage, ...entry.metadata }
    })

    Sentry.captureException(
      error instanceof Error ? error : new Error(`[init:${step}] ${errorMessage}`),
      {
        tags: {
          init_step: step,
          platform,
          init_phase: 'startup'
        },
        extra: {
          durationMs: entry.durationMs,
          errorStack,
          stepMetadata: entry.metadata,
          previousSteps: this.steps.filter(s => s.step !== step).map(s => ({
            step: s.step,
            success: s.success,
            durationMs: s.durationMs
          }))
        }
      }
    )
  }

  /**
   * Reporta la inicialización completa exitosa a Sentry.
   */
  reportSuccess (): void {
    const totalDuration = performance.now() - this.globalStart

    Sentry.captureEvent({
      message: 'Mobile Init Success',
      level: 'info',
      tags: {
        platform,
        init_phase: 'startup',
        init_result: 'success'
      },
      extra: {
        totalDurationMs: totalDuration.toFixed(0),
        steps: this.steps.map(s => ({
          step: s.step,
          success: s.success,
          durationMs: s.durationMs?.toFixed(0),
          metadata: s.metadata
        }))
      }
    })

    console.log(`[InitTracker] Init complete in ${totalDuration.toFixed(0)}ms`, this.getSummary())
  }

  /**
   * Reporta un fallo fatal de inicialización (la app no pudo arrancar).
   */
  reportFatalFailure (error: unknown): void {
    const totalDuration = performance.now() - this.globalStart
    const errorMessage = error instanceof Error ? error.message : String(error)

    Sentry.captureException(
      error instanceof Error ? error : new Error(`[init:fatal] ${errorMessage}`),
      {
        tags: {
          platform,
          init_phase: 'startup',
          init_result: 'fatal_failure'
        },
        extra: {
          totalDurationMs: totalDuration.toFixed(0),
          lastSuccessfulStep: this.getLastSuccessfulStep(),
          failedSteps: this.steps.filter(s => !s.success).map(s => s.step),
          allSteps: this.steps.map(s => ({
            step: s.step,
            success: s.success,
            durationMs: s.durationMs?.toFixed(0),
            error: s.error,
            metadata: s.metadata
          }))
        }
      }
    )
  }

  /**
   * Ejecuta un paso con tracking automático (start/end/fail).
   */
  async trackStep<T> (step: InitStep, fn: () => Promise<T>, metadata?: Record<string, any>): Promise<T> {
    this.startStep(step, metadata)
    try {
      const result = await fn()
      this.endStep(step)
      return result
    } catch (error) {
      this.failStep(step, error, metadata)
      throw error
    }
  }

  /**
   * Ejecuta un paso sync con tracking automático.
   */
  trackStepSync<T> (step: InitStep, fn: () => T, metadata?: Record<string, any>): T {
    this.startStep(step, metadata)
    try {
      const result = fn()
      this.endStep(step)
      return result
    } catch (error) {
      this.failStep(step, error, metadata)
      throw error
    }
  }

  private getLastSuccessfulStep (): string | undefined {
    const successful = this.steps.filter(s => s.success)
    return successful.length > 0 ? successful[successful.length - 1].step : undefined
  }

  private getSummary (): Record<string, string> {
    const summary: Record<string, string> = {}
    for (const s of this.steps) {
      summary[s.step] = s.success ? `✓ ${s.durationMs?.toFixed(0)}ms` : `✗ ${s.error}`
    }
    return summary
  }
}

// Singleton para toda la sesión de init
let instance: InitTracker | undefined

export function getInitTracker (): InitTracker {
  if (instance === undefined) {
    instance = new InitTracker()
  }
  return instance
}

export function resetInitTracker (): void {
  instance = new InitTracker()
}
